import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const SECTIONS = [
  {
    title: "What are cookies?",
    body: [
      "Cookies are small text files placed on your device when you visit a website. They help the site function, remember your preferences, and — with your permission — help us understand how the site is used.",
    ],
  },
  {
    title: "The cookies we use",
    body: [
      "Strictly necessary — required for the website to function: security, load balancing, and remembering your cookie preferences. These cannot be switched off.",
      "Analytics — anonymous usage statistics (for example, which pages are visited and for how long) that help us improve the site. These are only set if you accept them.",
      "Marketing — used to measure the effectiveness of campaigns and, where applicable, to show relevant content. These are only set if you accept them.",
    ],
  },
  {
    title: "Your choices",
    body: [
      "If you are visiting from Europe, you will be shown a cookie notice on your first visit where you can accept all cookies, reject all non-essential cookies, or choose your own selection.",
      "You can change or withdraw your consent at any time using the Cookie Settings link in the footer of every page. You can also control cookies through your browser settings — most browsers let you block or delete cookies entirely.",
    ],
  },
  {
    title: "Third-party services",
    body: [
      "Some pages embed or link to third-party services (for example LinkedIn and YouTube). If you interact with those services, they may set their own cookies under their own privacy policies.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "We may update this policy from time to time to reflect changes in the cookies we use or in the law. Any updates will be posted on this page with a revised date.",
    ],
  },
];

const CookiePolicy = () => (
  <main data-testid="cookie-policy-page" className="pt-20 bg-black">
    <article className="rounded-t-[2.5rem] bg-white text-black py-24 lg:py-32 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <MaskedLine delay={0.1}>
          <Link
            to="/"
            data-testid="cookie-policy-back-link"
            className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-cyan-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </MaskedLine>
        <div className="mt-10">
          <Overline className="text-cyan-600">Legal</Overline>
        </div>
        <MaskedLine delay={0.2}>
          <h1 className="font-display tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4">
            Cookie Policy
          </h1>
        </MaskedLine>
        <p className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-6">
          Market Rounds Technologies · Last updated: September 2026
        </p>
        <div className="w-16 h-px bg-cyan-400 mt-8" />

        <Reveal className="mt-12 space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="font-display tracking-tight text-2xl">{s.title}</h2>
              <div className="space-y-4 mt-4">
                {s.body.map((p, i) => (
                  <p key={i} className="text-base text-zinc-600 leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
          <section>
            <h2 className="font-display tracking-tight text-2xl">Contact us</h2>
            <p className="text-base text-zinc-600 leading-relaxed mt-4">
              Questions about this policy or your data? Email{" "}
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="cookie-policy-email-link"
                className="text-cyan-600 underline decoration-cyan-400/40 underline-offset-4 hover:decoration-cyan-500"
              >
                {CONTACT.email}
              </a>{" "}
              or write to us at {CONTACT.address}.
            </p>
          </section>
        </Reveal>
      </div>
    </article>
  </main>
);

export default CookiePolicy;
